import React, { useMemo } from 'react';
import { useTask } from '../context/TaskContext';

function DonutChart({ data }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Aucune tâche encore.</p>;

  let offset = 0;
  const r = 54, cx = 70, cy = 70, stroke = 22;
  const circ = 2 * Math.PI * r;

  const slices = data.map(d => {
    const pct  = d.value / total;
    const dash = pct * circ;
    const slice = { ...d, pct, dash, offset };
    offset += dash;
    return slice;
  });

  return (
    <div className="donut-wrap">
      <svg width="140" height="140" style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg-hover)" strokeWidth={stroke} />
        {slices.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${s.dash} ${circ - s.dash}`}
            strokeDashoffset={-s.offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition: 'stroke-dasharray 0.8s ease' }}
          />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fill="var(--text-primary)" fontSize="18" fontWeight="800" fontFamily="JetBrains Mono">{total}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fill="var(--text-muted)" fontSize="10">tâches</text>
      </svg>
      <div className="donut-legend">
        {slices.map((s, i) => (
          <div key={i} className="legend-item">
            <span className="legend-dot" style={{ background: s.color }} />
            <span className="legend-label">{s.label}</span>
            <span className="legend-val" style={{ color: s.color, marginLeft: '1rem' }}>{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarChart({ data, maxVal, colorFn }) {
  return (
    <div className="bar-chart">
      {data.map((d, i) => {
        const h = maxVal ? Math.max(4, (d.value / maxVal) * 110) : 4;
        return (
          <div key={i} className="bar-group">
            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'JetBrains Mono', marginBottom: '0.25rem' }}>{d.value}</span>
            <div className="bar" style={{ height: h, background: colorFn(d, i) }} title={`${d.label}: ${d.value}`} />
            <span className="bar-label">{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function Analytics({ tasks, stats }) {
  const total = (stats.todo || 0) + (stats['in-progress'] || 0) + (stats.done || 0);
  const rate  = total ? Math.round(((stats.done || 0) / total) * 100) : 0;

  const overdue = useMemo(() => tasks.filter(t => {
    if (!t.dueDate || t.status === 'done') return false;
    return new Date(t.dueDate) < new Date();
  }).length, [tasks]);

  const highPri = tasks.filter(t => t.priority === 'high' && t.status !== 'done').length;

  const donutData = [
    { label: 'À faire',    value: stats.todo || 0,            color: 'var(--text-muted)' },
    { label: 'En cours',   value: stats['in-progress'] || 0,  color: 'var(--accent)' },
    { label: 'Terminées',  value: stats.done || 0,            color: 'var(--success)' },
  ];

  const priData = [
    { label: 'Faible',   value: tasks.filter(t => t.priority === 'low').length },
    { label: 'Moyenne',  value: tasks.filter(t => t.priority === 'medium').length },
    { label: 'Haute',    value: tasks.filter(t => t.priority === 'high').length },
  ];
  const priColors = ['var(--success)', 'var(--warning)', 'var(--danger)'];

  const catMap = {};
  tasks.forEach(t => { catMap[t.category] = (catMap[t.category] || 0) + 1; });
  const catData = Object.entries(catMap).sort((a, b) => b[1] - a[1]).slice(0, 7);
  const catMax  = catData[0]?.[1] || 1;

  return (
    <div className="tasks-area" style={{ paddingTop: '1.5rem' }}>
      {/* KPI Cards */}
      <div className="analytics-grid">
        {[
          { value: total,    label: 'Total des tâches',    sub: 'Toutes catégories', color: 'var(--accent)' },
          { value: `${rate}%`, label: 'Taux de complétion', sub: `${stats.done || 0} terminées`, color: 'var(--success)' },
          { value: stats['in-progress'] || 0, label: 'En cours', sub: 'Actuellement actives', color: 'var(--info)' },
          { value: overdue, label: 'En retard',            sub: 'Échéance dépassée', color: 'var(--danger)' },
          { value: highPri, label: 'Haute priorité',       sub: 'Non terminées',    color: 'var(--warning)' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="stat-card-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div className="stat-card-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        {/* Donut */}
        <div className="chart-card">
          <div className="chart-title">Répartition par statut</div>
          <DonutChart data={donutData} />
        </div>

        {/* Priority bars */}
        <div className="chart-card">
          <div className="chart-title">Répartition par priorité</div>
          <BarChart
            data={priData}
            maxVal={Math.max(...priData.map(d => d.value), 1)}
            colorFn={(d, i) => priColors[i]}
          />
        </div>
      </div>

      {/* Category breakdown */}
      {catData.length > 0 && (
        <div className="chart-card">
          <div className="chart-title">Tâches par catégorie</div>
          <div className="category-bars">
            {catData.map(([cat, count]) => (
              <div key={cat} className="cat-row">
                <span className="cat-name">{cat}</span>
                <div className="cat-track">
                  <div className="cat-fill" style={{ width: `${(count / catMax) * 100}%` }} />
                </div>
                <span className="cat-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completion progress */}
      <div className="chart-card">
        <div className="chart-title">Progression globale</div>
        <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{stats.done || 0} tâches terminées sur {total}</span>
          <span style={{ fontSize: '0.8rem', color: 'var(--accent)', fontWeight: 700, fontFamily: 'JetBrains Mono' }}>{rate}%</span>
        </div>
        <div className="progress-track" style={{ height: '10px' }}>
          <div className="progress-fill" style={{ width: `${rate}%` }} />
        </div>
        {rate === 100 && total > 0 && (
          <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--success)' }}>
            🎉 Félicitations ! Toutes les tâches sont terminées.
          </p>
        )}
      </div>
    </div>
  );
}
